'use client';

import { datadogRum } from '@datadog/browser-rum';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  enabled: boolean;
  env: string;
  service: string;
  site: string;
  version: string;
}

interface DatadogContextType {
  isInitialized: boolean;
}

const DatadogContext = createContext<DatadogContextType>({
  isInitialized: false,
});

export const useDatadog = () => useContext(DatadogContext);

export function DatadogProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: DatadogConfig;
}) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!config.applicationId || !config.clientToken) {
      console.error('Datadog configuration is missing required fields');
      return;
    }

    // Only initialize if not already initialized
    if (!isInitialized && config.enabled && typeof window !== 'undefined') {
      datadogRum.init({
        applicationId: config.applicationId,
        clientToken: config.clientToken,
        site: config.site,
        service: config.service,
        env: config.env,
        version: config.version,
        trackUserInteractions: false, // Disable RUM features, only track errors
        defaultPrivacyLevel: 'mask-user-input',
      });

      // Only initialize error tracking, not full RUM
      datadogRum.startSessionReplayRecording = () => {};

      setIsInitialized(true);
      console.debug('Datadog Browser Error Tracking initialized');
    }
  }, [config, isInitialized]);

  return (
    <DatadogContext.Provider value={{ isInitialized }}>
      {children}
    </DatadogContext.Provider>
  );
}
